import { Input, VStack } from "@chakra-ui/react"
import { Field } from "@hydrologiq/shared/chakra/Field"
import { NativeSelectField, NativeSelectRoot } from "@hydrologiq/shared/chakra/NativeSelect"
import { AssetCategory, AssetClass, AssetKind, AssetRole } from "@hydrologiq/gql-experience/graphql"
import * as gqlTypes from "@hydrologiq/gql-experience/graphql"
import { useState } from "react"
import Form from "@hydrologiq/shared/chakra/custom/Form"
import ErrorAlert from "@hydrologiq/shared/components/ErrorAlert"
import { showSuccessToast, showErrorToast, showLoadingToast, dismissToast } from "@hydrologiq/shared/utils/toast"
import { useCopyValues } from "@hydrologiq/shared/providers/helpers/useCopy"
import { COPY_KEYS, DEFAULT_COPY_MAPPINGS } from "@hydrologiq/shared/copy/constants"
import { useMutationWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"
import { useGetFeature } from "@hydrologiq/shared/providers/helpers/useFeature"

/**
 * Asset class options displayed in the "Type" dropdown.
 * Maps asset_class values to user-friendly labels.
 */
const ASSET_CLASS_OPTIONS: { label: string; value: AssetClass }[] = [
  { label: "Generator", value: AssetClass.Genset },
  { label: "Solar", value: AssetClass.Solar },
  { label: "Battery", value: AssetClass.Battery },
  { label: "Inverter/Charger", value: AssetClass.InverterCharger },
  { label: "Fuel storage", value: AssetClass.Fuel },
  { label: "Storage", value: AssetClass.Storage },
  { label: "Custom", value: AssetClass.Hybrid },
  { label: "Distro", value: AssetClass.Distro },
  { label: "Refueller", value: AssetClass.Refueller },
  { label: "Electrolyser", value: AssetClass.Electrolyser },
]

/**
 * Sentinel value used in the "Type" dropdown for a Component asset.
 * A Component is an AssetClass, but its create flow differs from the other
 * classes (it exposes Kind + Role selectors), so it gets its own sentinel
 * option. Selecting it fixes class=Component and role=Load; kind is DC or AC.
 */
const COMPONENT_TYPE = "component" as const

/**
 * Kind (current-type) options available for a Component asset: a DC Load, and an
 * AC Load when the `ac_load` feature is on. An AC Load must not be mapped in
 * production until Load Precedence is live end to end
 * (docs/assets/ac-load-component/STATUS.md).
 */
const DC_LOAD_OPTION = { label: "DC", value: AssetKind.Dc }
const AC_LOAD_OPTION = { label: "AC", value: AssetKind.Ac }

/**
 * Role options available for a Component asset.
 * Only Load is selectable in v1.
 */
const COMPONENT_ROLE_OPTIONS: { label: string; value: AssetRole }[] = [{ label: "Load", value: AssetRole.Load }]

/**
 * Subtypes (asset_kind) available for the "Fuel storage" class.
 */
const FUEL_SUBTYPE_OPTIONS: { label: string; value: AssetKind }[] = [
  { label: "Manifold Cylinder Pallet", value: AssetKind.ManifoldCylinderPallet },
  { label: "Tube Trailer", value: AssetKind.TubeTrailer },
  { label: "Dynamic Storage", value: AssetKind.DynamicStorage },
]

export interface AssetCreationFormProps {
  onSuccess: (asset: gqlTypes.Asset) => void
  onCancel?: () => void
  onBack?: () => void
  heading?: string
  helperText?: string
}

/**
 * Shared asset creation form component.
 * Users select a Type (asset_class), and if the type has subtypes (fuel),
 * they must also select a Subtype (asset_kind).
 */
const AssetCreationForm = ({
  onSuccess,
  onCancel,
  onBack,
  heading = "Create an asset",
  helperText = "Please provide asset information below.",
}: AssetCreationFormProps) => {
  const [error, setError] = useState<string | null>(null)
  const [selectedClass, setSelectedClass] = useState<AssetClass | typeof COMPONENT_TYPE | "">("")
  const componentKindOptions = useGetFeature("ac_load") ? [DC_LOAD_OPTION, AC_LOAD_OPTION] : [DC_LOAD_OPTION]
  const [selectedSource, setSelectedSource] = useState<AssetCategory | "">("")

  const copy = useCopyValues({
    [COPY_KEYS.HEADINGS.ASSET]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.ASSET],
    [COPY_KEYS.MESSAGES.CREATED]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.MESSAGES.CREATED],
    [COPY_KEYS.MESSAGES.SUCCESSFULLY_CREATED]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.MESSAGES.SUCCESSFULLY_CREATED],
    [COPY_KEYS.MESSAGES.CREATION_FAILED]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.MESSAGES.CREATION_FAILED],
    [COPY_KEYS.MESSAGES.VALIDATION_ERROR]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.MESSAGES.VALIDATION_ERROR],
    [COPY_KEYS.MESSAGES.ADDING_TO_SYSTEM]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.MESSAGES.ADDING_TO_SYSTEM],
    [COPY_KEYS.FORMS.PLEASE_PROVIDE]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.FORMS.PLEASE_PROVIDE],
    [COPY_KEYS.FORMS.PLEASE_SELECT]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.FORMS.PLEASE_SELECT],
    [COPY_KEYS.FORMS.ENTER]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.FORMS.ENTER],
    [COPY_KEYS.FORMS.SELECT]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.FORMS.SELECT],
    [COPY_KEYS.LABELS.NAME]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.LABELS.NAME],
    [COPY_KEYS.LABELS.TYPE]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.LABELS.TYPE],
    [COPY_KEYS.LABELS.VOLUME]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.LABELS.VOLUME],
    [COPY_KEYS.LABELS.MIN_PRESSURE]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.LABELS.MIN_PRESSURE],
    [COPY_KEYS.LABELS.ASSET_NAME]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.LABELS.ASSET_NAME],
    [COPY_KEYS.ACTIONS.CREATING]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.ACTIONS.CREATING],
    [COPY_KEYS.COMMON.A]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.COMMON.A],
  })

  const [createAsset, { loading }] = useMutationWithEndpoint(
    gqlTypes.CreateAssetDocument,
    {
      refetchQueries: ["GetSuggestedFulfilments", "AssetAvailabilityCalendar", "GetAssets"],
      awaitRefetchQueries: true,
      update(cache, { data }) {
        if (!data?.createAsset) return

        const newAsset = data.createAsset

        cache.modify({
          fields: {
            assets(existingAssetsRefs = { data: [], meta: {} }, { readField, toReference }) {
              if (!existingAssetsRefs.meta) return existingAssetsRefs

              const newAssetRef = toReference(newAsset)
              const exists = existingAssetsRefs.data?.some((ref: any) => readField("id", ref) === newAsset.id)
              if (exists) return existingAssetsRefs

              return {
                ...existingAssetsRefs,
                data: [newAssetRef, ...(existingAssetsRefs.data || [])],
                meta: {
                  ...existingAssetsRefs.meta,
                  total_count: (existingAssetsRefs.meta?.total_count || 0) + 1,
                },
              }
            },
          },
        })
      },
    },
    "primary"
  )

  const isComponent = selectedClass === COMPONENT_TYPE
  const showSource = selectedClass === AssetClass.Fuel || selectedClass === AssetClass.Genset || selectedClass === AssetClass.Refueller || selectedClass === AssetClass.Storage
  const showSubtype = selectedClass === AssetClass.Fuel && selectedSource === AssetCategory.Hydrogen

  /**
   * Sends the CreateAsset mutation and handles the success/error UX.
   * Shared by every asset type so the create flow behaves identically.
   */
  const submitAsset = async (assetInput: gqlTypes.AssetInput, assetName: string): Promise<boolean> => {
    const result = await createAsset({
      variables: { asset: assetInput },
    })

    if (result.errors && result.errors.length > 0) {
      let errorMessage =
        result.errors[0].message ||
        `Failed to create ${copy[COPY_KEYS.HEADINGS.ASSET].toLowerCase()}. Please try again.`

      // Provide user-friendly messages for known errors
      if (errorMessage.includes("idx_asset_tenant_name_active") || errorMessage.includes("duplicate key")) {
        errorMessage = "An asset with this name already exists. Please choose a different name."
      }

      setError(errorMessage)
      showErrorToast(copy[COPY_KEYS.MESSAGES.CREATION_FAILED], errorMessage)
      return false
    }

    if (result.data?.createAsset) {
      const createdAsset = result.data.createAsset
      showSuccessToast(
        `${copy[COPY_KEYS.HEADINGS.ASSET]} ${copy[COPY_KEYS.MESSAGES.CREATED]}`,
        `${copy[COPY_KEYS.HEADINGS.ASSET]} "${assetName}" ${copy[COPY_KEYS.MESSAGES.SUCCESSFULLY_CREATED]}.`
      )
      setError(null)
      onSuccess(createdAsset)
      return true
    }

    console.error("CreateAsset response:", result)
    const errorMessage = `Failed to create ${copy[
      COPY_KEYS.HEADINGS.ASSET
    ].toLowerCase()}. The server returned an unexpected response.`
    setError(errorMessage)
    showErrorToast(copy[COPY_KEYS.MESSAGES.CREATION_FAILED], errorMessage)
    return false
  }

  const handleSubmit = async (data: FormData) => {
    let loadingToastId: string | undefined
    try {
      setError(null)

      loadingToastId = showLoadingToast(
        `${copy[COPY_KEYS.ACTIONS.CREATING]} ${copy[COPY_KEYS.HEADINGS.ASSET]}`,
        copy[COPY_KEYS.MESSAGES.ADDING_TO_SYSTEM]
      )

      const name = data.get("name")?.toString()
      const rawClass = data.get("class")?.toString()
      const isComponentAsset = rawClass === COMPONENT_TYPE
      const assetClass = rawClass as AssetClass
      const subtype = data.get("subtype")?.toString() as AssetKind | undefined
      const volumeStr = data.get("volume")?.toString()
      const minPressureStr = data.get("min_pressure")?.toString()

      if (!name || !name.trim()) {
        const errorMessage = `${copy[COPY_KEYS.FORMS.PLEASE_PROVIDE]} ${copy[COPY_KEYS.COMMON.A]} ${copy[
          COPY_KEYS.LABELS.ASSET_NAME
        ].toLowerCase()}`
        setError(errorMessage)
        showErrorToast(copy[COPY_KEYS.MESSAGES.VALIDATION_ERROR], errorMessage)
        return false
      }

      if (!assetClass) {
        const errorMessage = `${copy[COPY_KEYS.FORMS.PLEASE_SELECT]} ${copy[COPY_KEYS.COMMON.A]} ${copy[
          COPY_KEYS.LABELS.TYPE
        ].toLowerCase()}`
        setError(errorMessage)
        showErrorToast(copy[COPY_KEYS.MESSAGES.VALIDATION_ERROR], errorMessage)
        return false
      }

      // Component asset (class=Component): kind DC or AC, role Load.
      // Short-circuits the class-driven source/subtype derivation used by the
      // other asset types.
      if (isComponentAsset) {
        const componentKind = (data.get("component_kind")?.toString() as AssetKind) || AssetKind.Dc
        const componentRole = (data.get("role")?.toString() as AssetRole) || AssetRole.Load

        // Guard against values outside the v1 allowed sets (Property 5).
        const kindAllowed = componentKindOptions.some((opt) => opt.value === componentKind)
        const roleAllowed = COMPONENT_ROLE_OPTIONS.some((opt) => opt.value === componentRole)
        if (!kindAllowed || !roleAllowed) {
          const errorMessage = `${copy[COPY_KEYS.FORMS.PLEASE_SELECT]} a valid class and role`
          setError(errorMessage)
          showErrorToast(copy[COPY_KEYS.MESSAGES.VALIDATION_ERROR], errorMessage)
          return false
        }

        const componentInput = {
          name: name.trim(),
          kind: componentKind,
          category: AssetCategory.Power,
          class: AssetClass.Component,
          role: componentRole,
        }

        return await submitAsset(componentInput, name.trim())
      }

      // Determine category: user selects "Source" for fuel/generator/refueller, otherwise defaults to power
      let category: AssetCategory
      const sourceRequired = assetClass === AssetClass.Fuel || assetClass === AssetClass.Genset || assetClass === AssetClass.Refueller || assetClass === AssetClass.Storage
      if (sourceRequired) {
        const selectedSource = data.get("category")?.toString() as AssetCategory | undefined
        if (!selectedSource) {
          const errorMessage = `${copy[COPY_KEYS.FORMS.PLEASE_SELECT]} a source`
          setError(errorMessage)
          showErrorToast(copy[COPY_KEYS.MESSAGES.VALIDATION_ERROR], errorMessage)
          return false
        }
        category = selectedSource
      } else if (assetClass === AssetClass.Electrolyser) {
        category = AssetCategory.Other
      } else {
        category = AssetCategory.Power
      }

      // Determine the kind based on class
      let kind: AssetKind
      if (assetClass === AssetClass.Fuel && category === AssetCategory.Hydrogen) {
        if (!subtype) {
          const errorMessage = `${copy[COPY_KEYS.FORMS.PLEASE_SELECT]} a subtype`
          setError(errorMessage)
          showErrorToast(copy[COPY_KEYS.MESSAGES.VALIDATION_ERROR], errorMessage)
          return false
        }
        kind = subtype
      } else {
        kind = AssetKind.Other
      }

      // Parse optional numeric fields
      const volume = volumeStr && volumeStr.trim() ? parseFloat(volumeStr) : undefined
      const min_pressure = minPressureStr && minPressureStr.trim() ? parseFloat(minPressureStr) : undefined

      if (volume !== undefined && volume <= 0) {
        const errorMessage = `${copy[COPY_KEYS.LABELS.VOLUME]} must be greater than 0`
        setError(errorMessage)
        showErrorToast(copy[COPY_KEYS.MESSAGES.VALIDATION_ERROR], errorMessage)
        return false
      }

      if (min_pressure !== undefined && min_pressure < 0) {
        const errorMessage = `${copy[COPY_KEYS.LABELS.MIN_PRESSURE]} must be greater than or equal to 0`
        setError(errorMessage)
        showErrorToast(copy[COPY_KEYS.MESSAGES.VALIDATION_ERROR], errorMessage)
        return false
      }

      const assetInput = {
        name: name.trim(),
        kind,
        category,
        class: assetClass,
        ...(volume !== undefined && { volume }),
        ...(min_pressure !== undefined && { min_pressure }),
      }

      return await submitAsset(assetInput, name.trim())
    } catch (error) {
      let errorMessage =
        error instanceof Error
          ? error.message
          : `An unexpected error occurred while creating the ${copy[COPY_KEYS.HEADINGS.ASSET].toLowerCase()}`

      // Provide user-friendly messages for known errors
      if (errorMessage.includes("idx_asset_tenant_name_active") || errorMessage.includes("duplicate key")) {
        errorMessage = "An asset with this name already exists. Please choose a different name."
      }

      setError(errorMessage)
      showErrorToast(copy[COPY_KEYS.MESSAGES.CREATION_FAILED], errorMessage)
      return false
    } finally {
      if (loadingToastId) {
        dismissToast(loadingToastId)
      }
    }
  }

  const handleCancel = () => {
    setError(null)
    if (onCancel) onCancel()
  }

  const handleBack = () => {
    setError(null)
    if (onBack) onBack()
  }

  return (
    <Form
      heading={heading}
      helperText={helperText}
      actionType="create"
      actionContent={{ label: "Create", description: "Create the asset" }}
      loading={loading}
      onSubmit={handleSubmit}
      onCancel={onCancel ? handleCancel : undefined}
      onBack={onBack ? handleBack : undefined}
    >
      <VStack gap={5}>
        {error && (
          <ErrorAlert
            title={`${copy[COPY_KEYS.HEADINGS.ASSET]} Creation Error`}
            message={error}
            onClose={() => setError(null)}
          />
        )}
        <Field label={copy[COPY_KEYS.LABELS.NAME]} required>
          <Input
            name="name"
            placeholder={`${copy[COPY_KEYS.FORMS.ENTER] || "Enter"} ${(copy[COPY_KEYS.LABELS.ASSET_NAME] || copy[COPY_KEYS.LABELS.NAME] || "asset name").toLowerCase()}`}
          />
        </Field>

        <Field label={copy[COPY_KEYS.LABELS.TYPE]} required>
          <NativeSelectRoot>
            <NativeSelectField
              name="class"
              onChange={(e) => {
                setSelectedClass(e.target.value as AssetClass | "")
                setSelectedSource("")
              }}
              items={[
                {
                  label: `${copy[COPY_KEYS.FORMS.SELECT] || "Select"} ${(copy[COPY_KEYS.LABELS.TYPE] || "type").toLowerCase()}...`,
                  value: "",
                  disabled: false,
                },
                ...ASSET_CLASS_OPTIONS.map((opt) => ({ label: opt.label, value: opt.value })),
                { label: "Component", value: COMPONENT_TYPE },
              ]}
            />
          </NativeSelectRoot>
        </Field>

        {isComponent && (
          <>
            <Field label="Subtype" required>
              <NativeSelectRoot>
                <NativeSelectField
                  name="component_kind"
                  defaultValue={AssetKind.Dc}
                  items={componentKindOptions.map((opt) => ({ label: opt.label, value: opt.value }))}
                />
              </NativeSelectRoot>
            </Field>

            <Field label="Role" required>
              <NativeSelectRoot>
                <NativeSelectField
                  name="role"
                  defaultValue={AssetRole.Load}
                  items={COMPONENT_ROLE_OPTIONS.map((opt) => ({ label: opt.label, value: opt.value }))}
                />
              </NativeSelectRoot>
            </Field>
          </>
        )}

        {showSource && (
          <Field label="Source" required>
            <NativeSelectRoot>
              <NativeSelectField
                name="category"
                value={selectedSource}
                onChange={(e) => setSelectedSource(e.target.value as AssetCategory | "")}
                items={[
                  { label: "Select source...", value: "", disabled: false },
                  ...(selectedClass === AssetClass.Storage
                    ? [
                        { label: "Water", value: AssetCategory.Water },
                        { label: "Waste", value: AssetCategory.Waste },
                      ]
                    : [
                        { label: "Hydrogen", value: AssetCategory.Hydrogen },
                        { label: "Diesel", value: AssetCategory.Diesel },
                      ]),
                ]}
              />
            </NativeSelectRoot>
          </Field>
        )}

        {showSubtype && (
          <Field label="Subtype" required>
            <NativeSelectRoot>
              <NativeSelectField
                name="subtype"
                items={[
                  { label: "Select subtype...", value: "", disabled: false },
                  ...FUEL_SUBTYPE_OPTIONS.map((opt) => ({ label: opt.label, value: opt.value })),
                ]}
              />
            </NativeSelectRoot>
          </Field>
        )}

        {(selectedClass === AssetClass.Fuel || selectedClass === AssetClass.Storage) && (
          <>
            <Field label={copy[COPY_KEYS.LABELS.VOLUME]}>
              <Input
                name="volume"
                placeholder={`${copy[COPY_KEYS.FORMS.ENTER]} volume`}
                type="number"
                step="0.1"
                min="0.1"
              />
            </Field>

            <Field label={copy[COPY_KEYS.LABELS.MIN_PRESSURE]}>
              <Input
                name="min_pressure"
                placeholder={`${copy[COPY_KEYS.FORMS.ENTER]} minimum operating pressure`}
                type="number"
                step="0.1"
                min="0"
              />
            </Field>
          </>
        )}
      </VStack>
    </Form>
  )
}

export default AssetCreationForm
